import {
	Burger,
	Stack,
	UnstyledButton,
	Box,
	ScrollArea,
	Flex
} from "@mantine/core"
import LinksGroup from "./LinksGroup"
import type { LinkItem, LinkType } from "@models/link.model"
import styles from "./AppMainMenu.module.css"
import { useClickOutside, useDisclosure } from "@mantine/hooks"
import { AssistanceModal } from "@components/ui/AssistanceModal/AssistanceModal"
import { IconLink } from "./IconLink"
import { useAEMNavData } from "@AEM/hooks/useAEMNavData"
import { AEMPageType } from "@AEM/models/AEMPage.model"
import type { AEMLeafNode, AEMSectionNode } from "@AEM/models/AEMPage.model"
import { useState, useEffect, useRef } from "react"
import { AEMSvg } from "@components/AEMSvg"
import type { AEMSvgData } from "@components/AEMSvg"
import { Brands, BRANDS } from "@utils/constants.util"
import { useAppShellContext } from "@context/AppShellProvider"
import { useIsDesktop } from "@utils/device.utils"
import { svgToUrl } from "@utils/svgToUrl"
import { eventTrackingItems } from "@utils/eventTracking"
import { BrandSelectorModal } from "@components/ui/BrandSelectorModal/BrandSelectorModal"
import { useRedirect } from "@hooks/useRedirect"
import { useLocation, useNavigate } from "react-router-dom"
import { useAllowedTools } from "@hooks/useAllowedTools"

declare global {
	interface WindowEventMap {
		navigation_click: CustomEvent
		alert_bar_click: CustomEvent
		chat_bot_engagement: CustomEvent
		footer_click: CustomEvent
		hero_click: CustomEvent
		espresso_form_submission: CustomEvent
		callout_board_click: CustomEvent
		callout_board_promo_search: CustomEvent
		loyalty_rewards_click: CustomEvent
		group_leader_interaction: CustomEvent
		promotion_click: CustomEvent
		promotion_financial: CustomEvent
		header_click: CustomEvent
		financial_click: CustomEvent
		widget_interaction: CustomEvent
	}
}

function AppMainMenu({
	headerHeight,
	countdownBannerOpen
}: {
	headerHeight: number
	countdownBannerOpen: boolean
}) {
	const { data: collapsibleLinksComponentData } = useAEMNavData(
		AEMPageType.NAVIGATION_GLOBAL
	)
	const { data: iconLinksComponentData } = useAEMNavData(
		AEMPageType.NAVIGATION_SIDEBAR
	)

	const isDesktop = useIsDesktop()
	const { toggleMenu, openMenu, isMenuOpen } = useAppShellContext()
	const routeLocation = useLocation()
	const navigate = useNavigate()
	const [assistanceModalOpened, assistanceModalHandlers] = useDisclosure(false)
	const menuContentRef = useRef<HTMLDivElement>(null)
	const { isToolAllowed } = useAllowedTools()
	const ref = useClickOutside(() => {
		if (isMenuOpen && isDesktop) {
			toggleMenu()
		}
	})

	const MENU_HASH = "global-nav"
	const HELP_HASH = "help"

	// Focus first link when menu opens on mobile
	useEffect(() => {
		if (isMenuOpen && !isDesktop && menuContentRef.current) {
			const timer = setTimeout(() => {
				const firstFocusable =
					menuContentRef.current?.querySelector<HTMLElement>(
						'a[href], button:not([disabled]), [tabindex="0"]'
					)
				firstFocusable?.focus()
			}, 100)

			return () => clearTimeout(timer)
		}
	}, [isMenuOpen, isDesktop])

	const iconLinkElements: JSX.Element[] = []

	const [brandSelectorOpened, setBrandSelectorOpened] = useState(false)
	const [menuOpened] = useState(false)
	let contactUsIcon: AEMSvgData | undefined = undefined
	let helpIcon: AEMSvgData | undefined = undefined

	if (iconLinksComponentData) {
		const items = iconLinksComponentData[0]?.children.items
		for (const item of items) {
			if ("type" in item) {
				const linkItem: LinkItem = {
					icon: { path: svgToUrl(item.icon) } as AEMSvgData,
					label: item.label,
					link: item.link,
					toolCode: item.toolCode,
					type: item.type as LinkType,
					appId: item.appId
				}
				iconLinkElements.push(<IconLink key={item.label} item={linkItem} />)
			}
		}
		const contactUsNode =
			iconLinksComponentData[1]?.children.items[0] ?? undefined
		if (contactUsNode) {
			if ("icon" in contactUsNode) {
				contactUsIcon = {
					path: svgToUrl(contactUsNode.icon),
					title: contactUsNode.label
				} as AEMSvgData
			}
		}
		const helpNode = iconLinksComponentData[1]?.children.items[1] ?? undefined
		if (helpNode) {
			if ("icon" in helpNode) {
				helpIcon = {
					path: svgToUrl(helpNode.icon),
					title: helpNode.label
				} as AEMSvgData
			}
		}
	}

	const buildGlobalNav = (
		data: (AEMSectionNode | AEMLeafNode)[],
		opened: boolean
	): LinkItem[] => {
		const currentElements: LinkItem[] = []

		for (const item of data) {
			if ("children" in item) {
				if (item.children?.items) {
					const childLinks = buildGlobalNav(item.children.items, opened)
					if (childLinks.length > 0) {
						const element = {
							label: item.label,
							links: childLinks
						} as LinkItem
						currentElements.push(element)
					}
				}
			} else {
				const element = {
					label: item.label,
					link: item.link,
					toolCode: item.toolCode,
					type: item.type as LinkType,
					appId: item.appId
				} as LinkItem

				if (!element.toolCode || isToolAllowed(element.toolCode)) {
					currentElements.push(element)
				}
			}
		}
		return currentElements
	}

	const collapsibleLinkElements: JSX.Element[] = []
	const collapsibleContactUsLinkElements: JSX.Element[] = []
	let cruisingPowerLink: LinkItem | undefined = undefined

	const bottomLinks = buildGlobalNav(
		collapsibleLinksComponentData?.[1]?.children.items ?? [],
		isMenuOpen
	)
	const contactUsLinks = bottomLinks[0]

	if (collapsibleLinksComponentData) {
		const items = collapsibleLinksComponentData[0]?.children.items
		const linkItems = buildGlobalNav(items, isMenuOpen)

		for (const item of linkItems) {
			collapsibleLinkElements.push(
				<LinksGroup
					key={item.label}
					menuOpened={false}
					label={item.label ?? ""}
					links={item.links}
				/>
			)
		}

		if (contactUsLinks) {
			collapsibleContactUsLinkElements.push(
				<LinksGroup
					key={contactUsLinks.label}
					menuOpened={menuOpened}
					label={contactUsLinks.label ?? ""}
					links={contactUsLinks.links}
				/>
			)
		}
		const helpLink = bottomLinks[1]
		if (helpLink) {
			cruisingPowerLink = helpLink
		}
	}

	const toggleMenuAndTrack = () => {
		const interactionType = !isMenuOpen ? "Widget Open" : "Widget Close"
		eventTrackingItems({
			event_name: "navigation_click",
			navLinkText: "",
			interactionType: interactionType,
			category: "Navigation",
			link_url: ""
		})
		toggleMenu()
	}

	const handleContactUsClick = () => {
		if (!isMenuOpen) {
			setBrandSelectorOpened(true)
		} else {
			setBrandSelectorOpened(!brandSelectorOpened)
		}
	}

	const handleCruisingHelp = () => {
		assistanceModalHandlers.open
		eventTrackingItems({
			event_name: "click",
			link_url: "",
			category: "",
			interactionType: "",
			navLinkText: ""
		})
	}
	const { handleRedirect } = useRedirect()

	useEffect(() => {
		if (routeLocation.hash === `#${MENU_HASH}` && !isMenuOpen) {
			openMenu()
		}

		if (routeLocation.hash === `#${HELP_HASH}`) {
			assistanceModalHandlers.open()
		}
	}, [routeLocation.hash, isMenuOpen, openMenu, assistanceModalHandlers])

	useEffect(() => {
		const shouldClean =
			(isMenuOpen && routeLocation.hash === `#${MENU_HASH}`) ||
			(assistanceModalOpened && routeLocation.hash === `#${HELP_HASH}`)

		if (shouldClean) {
			navigate(
				{
					pathname: routeLocation.pathname,
					search: routeLocation.search
				},
				{ replace: true }
			)
		}
	}, [
		isMenuOpen,
		assistanceModalOpened,
		navigate,
		routeLocation.pathname,
		routeLocation.search,
		routeLocation.hash
	])

	return (
		<Stack id="app-main-menu" mih={{ base: "100%" }} mt={{ base: 0 }} ref={ref}>
			<BrandSelectorModal
				opened={brandSelectorOpened}
				onClose={() => setBrandSelectorOpened(false)}
				brands={["R", "C"]}
				onSelect={(brand) => {
					const brandLinks = contactUsLinks.links as LinkItem[]
					const brandLink = brandLinks.find((item) =>
						brand === Brands.ROYAL
							? item.link?.includes(BRANDS.ROYAL)
							: item.link?.includes(BRANDS.CELEBRITY)
					)
					if (brandLink) {
						handleRedirect(brandLink)
						setBrandSelectorOpened(false)
					}
				}}
			/>
			<Burger
				opened={isMenuOpen}
				onClick={toggleMenuAndTrack}
				aria-label="Toggle navigation"
				visibleFrom="md"
				ml={19}
				mah={70}
				mih={70}
				className={styles.burger}
				color="var(--text-color-primary)"
				aria-expanded={isMenuOpen ? "true" : "false"}
			/>
			<Box className={styles.menuContent} ref={menuContentRef}>
				{isMenuOpen ? (
					<ScrollArea className={styles.scrollArea}>
						{collapsibleLinkElements}
					</ScrollArea>
				) : (
					<Stack gap={0} align="center">
						{isDesktop ? iconLinkElements : null}
					</Stack>
				)}
			</Box>
			{(isMenuOpen || isDesktop) && (
				<Box
					h={{
						base: headerHeight + 160,
						sm: headerHeight + (countdownBannerOpen ? 200 : 60),
						md: headerHeight + 60
					}}
				>
					<Flex className={styles.navBottom}>
						<UnstyledButton
							onClick={handleContactUsClick}
							className={styles.bottomButton}
							component={isMenuOpen ? "span" : "button"}
						>
							{contactUsIcon && (
								<AEMSvg
									ariaRole={isMenuOpen ? "img" : "button"}
									svgData={contactUsIcon}
								/>
							)}
						</UnstyledButton>
						<Box
							className={styles.contactUs}
							display={isMenuOpen ? "block" : "none"}
						>
							{collapsibleContactUsLinkElements}
						</Box>
					</Flex>
					<Flex className={styles.navBottom}>
						<UnstyledButton
							onClick={assistanceModalHandlers.open}
							className={styles.bottomButton}
							component={isMenuOpen ? "span" : "button"}
							aria-label="Help And Assistance"
						>
							{helpIcon && (
								<AEMSvg
									ariaRole={isMenuOpen ? "img" : "button"}
									svgData={helpIcon}
								/>
							)}
						</UnstyledButton>
						<UnstyledButton
							onClick={handleCruisingHelp}
							className={styles.bottomAnchor}
							display={isMenuOpen ? "block" : "none"}
							ml={16}
						>
							{cruisingPowerLink?.label}
						</UnstyledButton>
					</Flex>
				</Box>
			)}
			<AssistanceModal
				opened={assistanceModalOpened}
				onClose={assistanceModalHandlers.close}
			/>
		</Stack>
	)
}

export default AppMainMenu
